import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceDashboardService } from '../../../services/maintenance-dashboard.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { AccountDateFilterRequest } from '../../../../../../_shared/models/accountDateFilter.model';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../../_shared/helpers/object.helper';
import { GroupedBarChartComponent } from '../../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { MaintenanceInsightsResponse } from '../../../models/maintenance-dashboard-response.model';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-assets-in-maintenance-modal',
  standalone: true,
  imports: [CommonModule, GroupedBarChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, DateFilterComponent, TranslocoPipe],
  templateUrl: './assets-in-maintenance-modal.component.html',
  styleUrl: './assets-in-maintenance-modal.component.scss'
})
export class AssetsInMaintenanceModalComponent implements OnInit {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private dashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  
  private filter: WidgetFilterData = { ...this.filterService.filter };

  chartData: GroupedChartData[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly subgroups = [
    { name: 'Missed', legend: this.transloco.translate('maintenanceOverview.calendar.missedService'), color: '#FF7F00' },
    { name: 'Completed', legend: this.transloco.translate('maintenanceDashboard.charts.completedService'), color: '#50AD41' }
  ];

  isLoading: boolean = true;
  title: string = this.transloco.translate('maintenanceDashboard.charts.assetsInMaintenance');
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  async ngOnInit(): Promise<void> {
    await this.loadMaintenanceData();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filter = {
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData;

      await this.loadMaintenanceData();
    }
  }

  async loadMaintenanceData(): Promise<void> {
    this.isLoading = true;

    try {
      const request: AccountDateFilterRequest = {
        accountIds: this.filter.accountIds,
        timeFrom: this.filter.startDate!,
        timeTo: this.filter.endDate!,
        interval: this.filter.interval,
      };

      const [summaryResponse, missedResponse, completedResponse] = await Promise.all([
        this.dashboardService.getAssetsInMaintenanceSummary({
          ...request,
          previousTimeFrom: this.filter.previousCycleStartDate!,
          previousTimeTo: this.filter.previousCycleEndDate!
        }, this.cancelSubject),
        this.dashboardService.getMissedServiceInsights(request, this.cancelSubject),
        this.dashboardService.getCompletedServiceInsights(request, this.cancelSubject)
      ]);

      const missedData = missedResponse.data ?? [];
      const completedData = completedResponse.data ?? [];
      const summary = summaryResponse.data;

      this.chartData = this.processResponses(missedData, completedData);
      this.yTicks = this.calculateYTicks(this.chartData);
      this.totalValue = summary?.totalValue ?? 0;
      this.percentageChange = summary?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processResponses(missedData: MaintenanceInsightsResponse[], completedData: MaintenanceInsightsResponse[]): GroupedChartData[] {
    if (!missedData && !completedData) return [];

    const daysDifference = differenceInDays(this.filter.endDate!, this.filter.startDate!);

    const datasets = [
      { data: missedData, label: 'Missed' },
      { data: completedData, label: 'Completed' }
    ];

    let groupedData: GroupedChartData[];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }
    // For 1 month (up to 31 days): show every day (no limit for modal)
    else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.filter.startDate!, this.filter.endDate!);
    }
    // For longer periods: group by weeks
    else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.filter.startDate!, this.filter.endDate!);
    }

    return groupedData;
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item['Missed']) || 0,
      Number(item['Completed']) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks, 6);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // Assets in maintenance increase is negative
  }

  formatValue(): string {
    return `${this.totalValue} Assets`;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}